import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { Progress } from '../../ui/progress';
import type { PayslipJob, PayslipJobsSummary } from './usePayslipUploadJobs';

interface PayslipUploadStepProps {
  jobs: PayslipJob[];
  running: boolean;
  summary: PayslipJobsSummary;
  onResolveConflict: (id: string, strategy: 'replace' | 'keep') => void;
  onReplaceAll: () => void;
  onRetry: (id: string) => void;
  onFinish: () => void;
}

/** Step 3: progress per employee, conflict resolution per row, final recap. */
export const PayslipUploadStep: React.FC<PayslipUploadStepProps> = ({ jobs, running, summary, onResolveConflict, onReplaceAll, onRetry, onFinish }) => {
  const { t } = useTranslation();
  const settled = summary.done + summary.errors;
  const percent = summary.total === 0 ? 0 : Math.round((settled / summary.total) * 100);
  const finished = !running && summary.conflicts === 0;

  const statusIcon = (job: PayslipJob) => {
    switch (job.status) {
      case 'done':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'conflict':
        return <AlertTriangle className="h-4 w-4 text-amber-600" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'uploading':
      case 'retrying':
        return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const statusText = (job: PayslipJob) => {
    switch (job.status) {
      case 'done':
        return [
          job.replaced ? t('documents.payslipUpload.upload.replaced', 'sostituita') : t('documents.payslipUpload.upload.created', 'caricata'),
          job.balances === 'failed' ? t('documents.payslipUpload.upload.balancesFailed', 'saldi non estratti') : null,
          job.userInactive ? t('documents.payslipUpload.match.inactive', 'cessato · nessuna notifica') : null,
        ]
          .filter(Boolean)
          .join(' · ');
      case 'conflict':
        return t('documents.payslipUpload.upload.exists', 'già presente per questo periodo');
      case 'error':
        return t(`documents.errors.${job.errorCode ?? 'generic'}`, t('documents.errors.generic', 'Caricamento non riuscito'));
      case 'retrying':
        return t('documents.payslipUpload.upload.retrying', 'in attesa, nuovo tentativo...');
      case 'uploading':
        return t('documents.payslipUpload.upload.uploading', 'caricamento...');
      default:
        return t('documents.payslipUpload.upload.pending', 'in coda');
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{t('documents.payslipUpload.upload.progress', '{{done}} di {{total}}', { done: settled, total: summary.total })}</span>
          {summary.conflicts > 1 && (
            <Button variant="outline" size="sm" onClick={onReplaceAll} disabled={running}>
              {t('documents.payslipUpload.upload.replaceAll', 'Sostituisci tutti')}
            </Button>
          )}
        </div>
        <Progress value={percent} />
      </div>

      <ul className="divide-y rounded-md border">
        {jobs.map((job) => (
          <li key={job.id} className="flex flex-col gap-2 p-3 text-sm sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {statusIcon(job)}
              <div className="min-w-0">
                <p className="truncate font-medium">{job.userName}</p>
                <p className="truncate text-xs text-muted-foreground">{statusText(job)}</p>
              </div>
            </div>
            {job.status === 'conflict' && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={running} onClick={() => onResolveConflict(job.id, 'keep')}>
                  {t('documents.payslipUpload.upload.keep', 'Mantieni entrambe')}
                </Button>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600" disabled={running} onClick={() => onResolveConflict(job.id, 'replace')}>
                  {t('documents.payslipUpload.upload.replace', 'Sostituisci')}
                </Button>
              </div>
            )}
            {job.status === 'error' && (
              <Button size="sm" variant="outline" disabled={running} onClick={() => onRetry(job.id)}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                {t('documents.payslipUpload.upload.retry', 'Riprova')}
              </Button>
            )}
          </li>
        ))}
      </ul>

      {finished && (
        <p className="text-sm text-muted-foreground">
          {t('documents.payslipUpload.upload.recap', 'Caricate {{created}}, sostituite {{replaced}}, errori {{errors}}.', {
            created: summary.done - summary.replaced,
            replaced: summary.replaced,
            errors: summary.errors,
          })}
          {summary.balancesFailed > 0 &&
            ` ${t('documents.payslipUpload.upload.balancesRecap', '{{count}} saldi non estratti: inseribili a mano.', { count: summary.balancesFailed })}`}
        </p>
      )}

      <div className="flex justify-end">
        <Button className="w-full bg-orange-500 hover:bg-orange-600 sm:w-auto" disabled={!finished} onClick={onFinish}>
          {t('documents.payslipUpload.upload.finish', 'Chiudi')}
        </Button>
      </div>
    </div>
  );
};
